/**
 * AI Theme Picker
 *
 * Theme selection grid for AI presentations.
 */

import React from 'react';
import { useAI } from './context/AIContext';
import { StyleTheme } from '../../ai/schemas/StyleTheme';

interface AIThemePickerProps {
  compact?: boolean;
  onSelect?: (theme: StyleTheme) => void;
}

export function AIThemePicker({ compact = false, onSelect }: AIThemePickerProps) {
  const { availableThemes, selectedTheme, setSelectedTheme } = useAI();

  const handleSelect = (theme: StyleTheme) => {
    setSelectedTheme(theme);
    onSelect?.(theme);
  };

  const displayThemes = compact ? availableThemes.slice(0, 6) : availableThemes;

  return (
    <div className={`ai-theme-picker ${compact ? 'ai-theme-picker-compact' : ''}`}>
      {!compact && <h4>Choose a Theme</h4>}
      <div className="ai-theme-grid">
        {displayThemes.map((theme) => (
          <ThemeCard
            key={theme.id}
            theme={theme}
            isSelected={selectedTheme.id === theme.id}
            onSelect={handleSelect}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Individual theme card
 */
function ThemeCard({
  theme,
  isSelected,
  onSelect,
}: {
  theme: StyleTheme;
  isSelected: boolean;
  onSelect: (theme: StyleTheme) => void;
}) {
  return (
    <button
      className={`ai-theme-card ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelect(theme)}
      style={{
        backgroundColor: theme.colors.background,
        borderColor: isSelected ? theme.colors.primary : 'transparent',
      }}
    >
      <div className="ai-theme-preview">
        <div
          className="ai-theme-title"
          style={{
            color: theme.colors.textPrimary,
            fontFamily: theme.typography.titleFont,
          }}
        >
          Aa
        </div>
        <div className="ai-theme-colors">
          <span style={{ backgroundColor: theme.colors.primary }} />
          <span style={{ backgroundColor: theme.colors.secondary }} />
          {theme.colors.accent[0] && (
            <span style={{ backgroundColor: theme.colors.accent[0] }} />
          )}
        </div>
      </div>
      <div
        className="ai-theme-name"
        style={{ color: theme.colors.textPrimary }}
      >
        {theme.name}
      </div>
      {theme.category && (
        <div
          className="ai-theme-category"
          style={{ color: theme.colors.textSecondary }}
        >
          {theme.category}
        </div>
      )}
    </button>
  );
}

export default AIThemePicker;
